import React, { useMemo, useState } from 'react';
import { ExternalLink, Search, Trash2, Eye, MapIcon, MessageCircle } from 'lucide-react';
import AdminHostDetailView from '../../pages/admin/AdminHostDetailView';
import { formatEuropeanDateTime } from '../../utils/dateFormat';
import { getListingPresentation } from '../../utils/hostListings';
import { buildListingMapItem } from './AdminGeoMapExplorer';
import { AdminGeoMapCanvas } from './AdminGeoMapCanvas';
import { Badge, Card, TimelinePreview } from './AdminPanelPrimitives';
import { StatusChip, STATUS_PALETTE } from './adminDesignSystem';

const isLocationUnavailable = (entry) => {
  const latitude = Number(entry?.listing_latitude);
  const longitude = Number(entry?.listing_longitude);
  return !entry?.listing_location_label || Number.isNaN(latitude) || Number.isNaN(longitude);
};

export const AdminListingsPanel = ({
  applications,
  ignoredIcalEvents,
  onApproveListing,
  onDeleteListing,
  expandedTimelineId,
  fetchHostDetail,
  hostDetail,
  hostDetailLoading,
  listingStatusFilter,
  mobileMode = false,
  onContactUser,
  onRejectListing,
  onStartConversation,
  onViewOnMap,
  searchQuery,
  selectedHost,
  setExpandedTimelineId,
  setHostDetail,
  setListingStatusFilter,
  setSearchQuery,
  setSelectedHost,
}) => {
  const [selectedMapId, setSelectedMapId] = useState('');
  const [ignoredReasonFilter, setIgnoredReasonFilter] = useState('all');
  const [ignoredSearchQuery, setIgnoredSearchQuery] = useState('');

  const ignoredIcalCount = (ignoredIcalEvents || []).length;

  const filteredByStatus = useMemo(() => applications.filter((entry) => {
    if (listingStatusFilter === 'active') return entry.status === 'approved';
    if (listingStatusFilter === 'early') return entry.status === 'rejected';
    if (listingStatusFilter === 'unavailable') return entry.status === 'pending' && isLocationUnavailable(entry);
    if (listingStatusFilter === 'ignored') return false;
    return false;
  }), [applications, listingStatusFilter]);

  const visibleListings = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return filteredByStatus.filter((entry) => {
      if (!query) return true;
      const listingMeta = getListingPresentation({ title: entry.listing_title, address: entry.listing_location_label });
      return [
        entry.user?.name,
        entry.user?.email,
        entry.user?.customer_id,
        entry.listing_title,
        listingMeta.displayName,
        listingMeta.displayAddress,
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
    });
  }, [filteredByStatus, searchQuery]);

  const mapItems = useMemo(() => visibleListings.map(buildListingMapItem).filter(Boolean), [visibleListings]);
  const selectedMapItemId = selectedMapId || mapItems[0]?.id || '';

  const visibleIgnoredEvents = useMemo(() => {
    const query = ignoredSearchQuery.trim().toLowerCase();
    return (ignoredIcalEvents || []).filter((entry) => {
      if (ignoredReasonFilter !== 'all' && entry.ignore_reason !== ignoredReasonFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      return [
        entry.listing_title,
        entry.listing_id,
        entry.summary,
        entry.host_user?.name,
        entry.host_user?.email,
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
    });
  }, [ignoredIcalEvents, ignoredReasonFilter, ignoredSearchQuery]);

  if (selectedHost) {
    return (
      <AdminHostDetailView
        detail={hostDetail}
        loading={hostDetailLoading}
        backLabel="Back"
        onBack={() => { setSelectedHost(null); setHostDetail(null); }}
        onRefresh={() => fetchHostDetail(selectedHost)}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: mobileMode ? '1fr' : 'minmax(0, 1.1fr) minmax(360px, 0.9fr)', gap: 14, alignItems: 'start' }}>
      {/* Mobile: map shown ABOVE the list */}
      {mobileMode && listingStatusFilter !== 'ignored' && (
        <AdminGeoMapCanvas
          dataTestId="admin-listings-inline-map-mobile"
          emptyCopy="Listings with coordinates will appear here."
          items={mapItems}
          onSelect={setSelectedMapId}
          selectedId={selectedMapItemId}
          title="Listings map"
          height={280}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Card style={{ marginBottom: 4 }}>
          <div style={{ flex: 1, minWidth: 260, display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #E7E7E7', borderRadius: 14, padding: '12px 14px' }}>
            <Search size={18} color="#666" />
            <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} data-testid="admin-listings-search-input" placeholder="Search listings by e-mail or Account ID" style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 15 }} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            <StatusChip tone="active" label="Active listings" count={applications.filter((entry) => entry.status === 'approved').length} active={listingStatusFilter === 'active'} onClick={() => setListingStatusFilter('active')} dataTestId="admin-listings-filter-active" />
            <StatusChip tone="early" label="Early listings" count={applications.filter((entry) => entry.status === 'rejected').length} active={listingStatusFilter === 'early'} onClick={() => setListingStatusFilter('early')} dataTestId="admin-listings-filter-early" />
            {(() => {
              const locUnavailableCount = applications.filter((entry) => entry.status === 'pending' && isLocationUnavailable(entry)).length;
              // Auto-detected coordinates from the Airbnb URL mean this
              // bucket is almost always empty. Hide the chip entirely
              // unless something actually needs review.
              if (locUnavailableCount === 0 && listingStatusFilter !== 'unavailable') return null;
              return (
                <StatusChip tone="urgent" label="Location unavailable" count={locUnavailableCount} active={listingStatusFilter === 'unavailable'} onClick={() => setListingStatusFilter('unavailable')} dataTestId="admin-listings-filter-unavailable" />
              );
            })()}
            <StatusChip tone="neutral" label="Ignored iCal activity" count={ignoredIcalCount} active={listingStatusFilter === 'ignored'} onClick={() => setListingStatusFilter('ignored')} dataTestId="admin-listings-filter-ignored" />
          </div>
        </Card>

        {listingStatusFilter === 'ignored' ? (
        <Card data-testid="admin-ignored-ical-events-card" style={{ marginBottom: 4 }}>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 700, color: '#111', margin: 0 }}>Ignored iCal activity</p>
                <p style={{ fontSize: 13, color: '#666', margin: '6px 0 0', lineHeight: 1.35 }}>Internal-only view for repeated Airbnb availability-block patterns that were ignored before reaching hosts.</p>
              </div>
              <Badge text={`${visibleIgnoredEvents.length}`} color="blue" />
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220, display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #E7E7E7', borderRadius: 14, padding: '10px 12px' }}>
                <Search size={16} color="#666" />
                <input
                  value={ignoredSearchQuery}
                  onChange={(event) => setIgnoredSearchQuery(event.target.value)}
                  data-testid="admin-ignored-ical-search-input"
                  placeholder="Search by listing, host, or summary"
                  style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14 }}
                />
              </div>

              <select
                value={ignoredReasonFilter}
                onChange={(event) => setIgnoredReasonFilter(event.target.value)}
                data-testid="admin-ignored-ical-reason-filter"
                style={{ minWidth: 210, borderRadius: 14, border: '1px solid #E7E7E7', background: '#FFF', padding: '10px 12px', fontSize: 14 }}
              >
                <option value="all">All reasons</option>
                <option value="availability_block_summary">Availability block summary</option>
              </select>
            </div>

            {visibleIgnoredEvents.length === 0 ? <p style={{ margin: 0, color: '#666', fontSize: 14 }}>No ignored iCal activity matched these filters.</p> : null}

            <div style={{ display: 'grid', gap: 10 }}>
              {visibleIgnoredEvents.slice(0, 24).map((entry) => (
                <div key={entry.id} data-testid={`admin-ignored-ical-row-${entry.id}`} style={{ border: '1px solid #E7E7E7', borderRadius: 16, padding: '14px 14px 12px', display: 'grid', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111' }}>{entry.listing_title || entry.listing_id || 'Listing'}</p>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>{entry.host_user?.name || 'Host'}{entry.host_user?.email ? ` · ${entry.host_user.email}` : ''}</p>
                    </div>
                    <Badge text={(entry.ignore_reason || 'ignored').replaceAll('_', ' ')} color="orange" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>Summary</p>
                      <p style={{ margin: '3px 0 0', fontSize: 13, color: '#111', lineHeight: 1.3 }}>{entry.summary || '—'}</p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>Seen count</p>
                      <p style={{ margin: '3px 0 0', fontSize: 13, color: '#111', lineHeight: 1.3 }}>{entry.seen_count || 1}</p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>Dates</p>
                      <p style={{ margin: '3px 0 0', fontSize: 13, color: '#111', lineHeight: 1.3 }}>{entry.check_in_date || '—'} → {entry.checkout_date || '—'}</p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>Last seen</p>
                      <p style={{ margin: '3px 0 0', fontSize: 13, color: '#111', lineHeight: 1.3 }}>{formatEuropeanDateTime(entry.last_seen_at || entry.updated_at || entry.created_at)}</p>
                    </div>
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: '#666', lineHeight: 1.4 }}>
                    Settings snapshot · Notice {entry.settings_snapshot?.airbnb_advance_notice || 'same_day'} · Same-day cutoff {entry.settings_snapshot?.airbnb_same_day_cutoff_hour ?? '—'} · Prep {entry.settings_snapshot?.airbnb_preparation_time_nights ?? 0}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Card>
        ) : null}

        {listingStatusFilter !== 'ignored' && visibleListings.length === 0 ? <Card><p style={{ margin: 0, color: '#666' }}>No listing cards found for this group.</p></Card> : null}

        {listingStatusFilter !== 'ignored' && visibleListings.map((entry) => {
          const listingMeta = getListingPresentation({ title: entry.listing_title, address: entry.listing_location_label });
          const listingTone = entry.status === 'approved' ? 'active' : entry.status === 'rejected' ? 'early' : 'pending';
          const listingPalette = STATUS_PALETTE[listingTone];
          const listingBadge = entry.status === 'approved' ? 'ACTIVE' : entry.status === 'rejected' ? 'EARLY' : 'REVIEW';
          const isSelectedCard = selectedMapItemId === `listing-${entry.id}`;
          const showApprovalRow = entry.status === 'pending' && isLocationUnavailable(entry);
          return (
            <Card
              key={entry.id}
              data-testid={`admin-listing-card-${entry.id}`}
              style={{
                cursor: 'pointer',
                padding: 0,
                overflow: 'hidden',
                border: isSelectedCard ? `1px solid ${listingPalette.solid}66` : '1px solid #E7E7E7',
                boxShadow: isSelectedCard ? `0 10px 24px ${listingPalette.solid}1f` : '0 4px 12px rgba(17,17,17,0.04)',
                position: 'relative',
              }}
            >
              <div onClick={() => setSelectedMapId(`listing-${entry.id}`)} style={{ display: 'flex', alignItems: 'stretch' }}>
                <div aria-hidden="true" style={{ width: 5, background: listingPalette.solid, flexShrink: 0 }} />
                <div style={{ flex: 1, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#0F1115' }}>{listingMeta.displayName}</p>
                      <p style={{ fontSize: 12, color: '#6B7280', margin: '3px 0 0' }}>
                        {listingMeta.displayAddress || 'Approximate listing location unavailable'}
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: 11.5, color: '#9CA3AF' }}>
                        <span style={{ color: '#374151', fontWeight: 600 }}>{entry.user?.name || entry.user?.email || 'Host'}</span>
                        {entry.user?.email ? ` · ${entry.user.email}` : ''}
                        {' · '}
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: '#374151' }}>{entry.user?.customer_id || '—'}</span>
                      </p>
                    </div>
                    <Badge text={listingBadge} tone={listingTone} />
                  </div>

                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    {entry.airbnb_url ? (
                      <a href={entry.airbnb_url} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()} data-testid={`admin-listing-url-${entry.id}`} style={{ fontSize: 12, color: '#075985', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 9999, border: '1px solid #BAE6FD', background: '#E0F2FE', textDecoration: 'none', fontWeight: 600 }}>
                        <ExternalLink size={11} /> Listing URL
                      </a>
                    ) : null}
                    {isLocationUnavailable(entry) ? <Badge text="LOCATION UNAVAILABLE" tone="urgent" /> : null}
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={(event) => { event.stopPropagation(); fetchHostDetail(entry.user_id); }}
                      data-testid={`admin-listing-open-details-${entry.id}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: 'none', background: '#0F1115', color: '#FFFFFF', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
                    >
                      <Eye size={14} /> Open details
                    </button>
                    {!isLocationUnavailable(entry) ? (
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); onViewOnMap(`listing-${entry.id}`, [entry.status === 'approved' ? 'active-listings' : 'early-listings']); }}
                        data-testid={`admin-listing-view-on-map-${entry.id}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1px solid #E5E7EB', background: '#FFFFFF', color: '#3B404B', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                      >
                        <MapIcon size={14} /> Map
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={(event) => { event.stopPropagation(); onContactUser(entry.user_id); }}
                      data-testid={`admin-listing-contact-user-${entry.id}`}
                      style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1px solid #E5E7EB', background: '#FFFFFF', color: '#3B404B', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                    >
                      <MessageCircle size={14} /> Contact
                    </button>
                  </div>

                  {showApprovalRow ? (
                    <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', padding: '8px 10px', borderRadius: 10, background: STATUS_PALETTE.pending.soft, border: `1px solid ${STATUS_PALETTE.pending.solid}33` }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: STATUS_PALETTE.pending.text, textTransform: 'uppercase', letterSpacing: 0.4, alignSelf: 'center', marginRight: 4 }}>Review needed</span>
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); onApproveListing(entry.id); }}
                        data-testid={`admin-listing-approve-review-${entry.id}`}
                        style={{ fontSize: 12, color: '#FFF', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: 'none', background: STATUS_PALETTE.active.solid, cursor: 'pointer', fontWeight: 700 }}
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); onRejectListing(entry.id); }}
                        data-testid={`admin-listing-early-review-${entry.id}`}
                        style={{ fontSize: 12, color: '#FFF', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: 'none', background: STATUS_PALETTE.early.solid, cursor: 'pointer', fontWeight: 700 }}
                      >
                        Mark Early
                      </button>
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); onDeleteListing(entry.id); }}
                        data-testid={`admin-listing-decline-review-${entry.id}`}
                        style={{ fontSize: 12, color: '#FFF', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: 'none', background: STATUS_PALETTE.urgent.solid, cursor: 'pointer', fontWeight: 700 }}
                      >
                        Decline
                      </button>
                    </div>
                  ) : (
                    <div style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); onDeleteListing(entry.id); }}
                        data-testid={`admin-listing-delete-${entry.id}`}
                        style={{ fontSize: 12, color: STATUS_PALETTE.urgent.text, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 9999, border: `1px solid ${STATUS_PALETTE.urgent.solid}33`, background: STATUS_PALETTE.urgent.soft, cursor: 'pointer', fontWeight: 700 }}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  )}

                  <TimelinePreview
                    timelineId={`listing-${entry.id}`}
                    expandedTimelineId={expandedTimelineId}
                    setExpandedTimelineId={setExpandedTimelineId}
                    items={[
                      { label: 'Latest listing update', value: formatEuropeanDateTime(entry.updated_at || entry.created_at) },
                      { label: 'Host access', value: entry.status },
                      { label: 'Account created', value: formatEuropeanDateTime(entry.user?.created_at) },
                    ]}
                  />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Desktop: map in right column; Mobile: shown above instead */}
      {!mobileMode && (listingStatusFilter !== 'ignored' ? <AdminGeoMapCanvas
        dataTestId="admin-listings-inline-map"
        emptyCopy="Listings with coordinates will appear here."
        items={mapItems}
        onSelect={setSelectedMapId}
        selectedId={selectedMapItemId}
        title="Listings map"
      /> : <Card><p style={{ margin: 0, color: '#666' }}>Ignored iCal activity is an internal observation category and does not appear on the map.</p></Card>)}
    </div>
  );
};

export default AdminListingsPanel;